import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { EmailThread, EmailThreadList, EmailThreadType } from '@features/email/interfaces/email-thread.interface';
import { Store } from '@ngrx/store';
import { authenticationQuery } from '@shared/state/selectors/authentication/authentication.selectors';
import { Observable, switchMap } from 'rxjs';

export interface CreateEmailThreadDto {
  type: EmailThreadType;
  threadId: string;
  messageId?: string;
  senderEmail?: string;
  recordGenerator?: string;
  enquiryUuid?: string;
  listingUuid?: string;
  /** 0 = Block, 1 = Allow */
  status?: number;
  emailProvider?: string;
  created?: string;
  updated?: string;
}

@Injectable({ providedIn: 'root' })
export class EmailThreadsService {
  constructor(
    private _http: HttpClient,
    private _store: Store
  ) {}

  /**
   * Returns email thread records for the authenticated supplier, optionally filtered by
   * comma-separated threadId(s) or enquiryUuid(s).
   */
  getEmailThreads$(params: { threadId?: string; messageId?: string; enquiryUuid?: string; listingUuid?: string; limit?: number; page?: number; type?: string }): Observable<EmailThreadList> {
    let p = new HttpParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') p = p.set(k, String(v));
    });
    return this._store
      .select(authenticationQuery.getUserUuid)
      .pipe(
        switchMap(userId => this._http.get<EmailThreadList>(`${environment.supplierApiGateway}/sup/account/${userId}/email-threads`, { params: p }))
      );
  }

  /**
   * Creates (or upserts) an email thread record linking a Gmail thread to an enquiry.
   */
  createEmailThread$(dto: CreateEmailThreadDto): Observable<EmailThread> {
    return this._store
      .select(authenticationQuery.getUserUuid)
      .pipe(switchMap(userId => this._http.post<EmailThread>(`${environment.supplierApiGateway}/sup/account/${userId}/email-threads`, dto)));
  }

  /**
   * Updates mutable fields on an existing email thread record (isUnread, updated).
   * 404 means the thread has no DB record yet — callers should handle gracefully.
   */
  updateEmailThread$(dto: { threadId: string; isUnread?: boolean; updated?: string }): Observable<{ success: boolean; threadId: string }> {
    return this._store
      .select(authenticationQuery.getUserUuid)
      .pipe(switchMap(userId => this._http.patch<{ success: boolean; threadId: string }>(`${environment.supplierApiGateway}/sup/account/${userId}/email-threads`, dto)));
  }

  /**
   * Updates mutable fields (isUnread, updated) on multiple email thread records in one request.
   */
  batchUpdateEmailThreads$(items: { threadId: string; isUnread?: boolean; updated?: string }[]): Observable<{ success: boolean; threadIds: string[] }> {
    return this._store
      .select(authenticationQuery.getUserUuid)
      .pipe(
        switchMap(userId =>
          this._http.patch<{ success: boolean; threadIds: string[] }>(`${environment.supplierApiGateway}/sup/account/${userId}/email-threads/batch-update`, { items })
        )
      );
  }
}
